import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import { isAnalyticsUser } from '../../config/api';
import { ActiveTab, PeriodConfig } from './types';
import PeriodSelector from './components/PeriodSelector';
import TabBar from './components/TabBar';

// Lazy-load tabs to avoid loading everything at once
import HomeTab from './tabs/HomeTab';
import OverviewTab from './tabs/OverviewTab';
import RealtimeTab from './tabs/RealtimeTab';
import UsersTab from './tabs/UsersTab';
import VideosTab from './tabs/VideosTab';
import EngagementTab from './tabs/EngagementTab';
import TopUsersTab from './tabs/TopUsersTab';
import GeographyTab from './tabs/GeographyTab';
import AiLensTab from './tabs/AiLensTab';
import DevicesTab from './tabs/DevicesTab';
import ScansTab from './tabs/ScansTab';
import SubscriptionsTab from './tabs/SubscriptionsTab';
import RevenueTab from './tabs/RevenueTab';
import UserJourneyTab from './tabs/UserJourneyTab';
import UserSegmentsTab from './tabs/UserSegmentsTab';
import LeaderboardTab from './tabs/LeaderboardTab';
import CountryAnalysisTab from './tabs/CountryAnalysisTab';
import AdsRoiTab from './tabs/AdsRoiTab';
import DatabaseTab from './tabs/DatabaseTab';

const AnalyticsV2: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<ActiveTab>('home');
  const [period, setPeriod] = useState<PeriodConfig>({ type: 'live' });

  useEffect(() => {
    if (!isAnalyticsUser()) {
      navigate('/dashboard');
    }
  }, [navigate]);

  const renderTab = () => {
    switch (activeTab) {
      case 'home': return <HomeTab period={period} />;
      case 'overview': return <OverviewTab period={period} />;
      case 'realtime': return <RealtimeTab period={period} />;
      case 'users': return <UsersTab period={period} />;
      case 'videos': return <VideosTab period={period} />;
      case 'engagement': return <EngagementTab period={period} />;
      case 'topUsers': return <TopUsersTab period={period} />;
      case 'geography': return <GeographyTab period={period} />;
      case 'aiLens': return <AiLensTab period={period} />;
      case 'devices': return <DevicesTab period={period} />;
      case 'scans': return <ScansTab />;
      case 'subscriptions': return <SubscriptionsTab period={period} />;
      case 'revenue': return <RevenueTab period={period} />;
      case 'userJourney': return <UserJourneyTab period={period} />;
      case 'userSegments': return <UserSegmentsTab period={period} />;
      case 'leaderboard': return <LeaderboardTab period={period} />;
      case 'countryAnalysis': return <CountryAnalysisTab period={period} />;
      case 'adsRoi': return <AdsRoiTab period={period} />;
      case 'database': return <DatabaseTab />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#05060A]">
      <Navbar />
      <div className="max-w-[1920px] mx-auto px-3 sm:px-6 pt-3 sm:pt-4 pb-6 sm:pb-8">
        {/* Period + Refresh + Switch to Classic (all in one compact row) */}
        <PeriodSelector
          period={period}
          onChange={setPeriod}
          onSwitchToClassic={() => navigate('/analytics')}
        />

        {/* Tab Bar */}
        <TabBar activeTab={activeTab} onTabChange={setActiveTab} />

        {/* Active Tab Content */}
        {renderTab()}
      </div>
    </div>
  );
};

export default AnalyticsV2;
